import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import '../styles/login.css';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext.tsx';
import { FaSyncAlt, FaUserAlt } from "react-icons/fa";
import { RiLockPasswordFill } from "react-icons/ri";
import { logUserActivity } from '../services/player/LoginService.tsx';
import { clientIpInfo } from '../services/player/apis/Schelude.tsx';
import { UserIPData } from '../models/UserActivity.tsx';
import { applyLoginChrome, clearLoginChrome } from '../helpers/loginChrome.ts';

const loginBrandLogo = require("../images/jocksports-login-logo.png");
const loginMobileBg = require("../images/login-mobile-bg.png");

const Login = () => {

    const { setUserIpData, login, error } = useUser();

    const navigate = useNavigate();
    const userNameRef = useRef<HTMLInputElement>(null);
    const pinRef = useRef<HTMLInputElement>(null);
    const dismissTimersRef = useRef<number[]>([]);
    const [userName, setUserName] = useState('');
    const [pin, setPin] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [logoReady, setLogoReady] = useState(false);

    const dismissKeyboard = useCallback(() => {
        const active = document.activeElement;
        if (active instanceof HTMLElement) {
            active.blur();
        }
        userNameRef.current?.blur();
        pinRef.current?.blur();
        window.scrollTo(0, 0);
    }, []);

    const dismissKeyboardAfterFaceId = useCallback(() => {
        dismissKeyboard();
        // iOS restores focus (and the keyboard) after the Face ID sheet closes.
        [50, 150, 350].forEach((ms) => {
            const id = window.setTimeout(dismissKeyboard, ms);
            dismissTimersRef.current.push(id);
        });
    }, [dismissKeyboard]);

    useEffect(() => {
        return () => {
            dismissTimersRef.current.forEach((id) => window.clearTimeout(id));
        };
    }, []);

    // Stabilize login surface before first paint (avoids height/chrome bounce on resume).
    useLayoutEffect(() => {
        applyLoginChrome();
        const root = document.documentElement;
        root.classList.remove("login-keyboard-open");
        root.style.setProperty("--login-screen-height", `${window.innerHeight}px`);
        const body = document.querySelector(".bodylogin") as HTMLElement | null;
        if (body) body.style.transform = "";
        window.scrollTo(0, 0);

        // Preload brand logo early so the header does not pop in after paint.
        const preload = document.createElement("link");
        preload.rel = "preload";
        preload.as = "image";
        preload.href = loginBrandLogo;
        document.head.appendChild(preload);

        const warm = new Image();
        warm.decoding = "sync";
        warm.onload = () => setLogoReady(true);
        warm.onerror = () => setLogoReady(true);
        warm.src = loginBrandLogo;
        if (warm.complete) setLogoReady(true);

        return () => {
            preload.remove();
            clearLoginChrome();
        };
    }, []);

    useEffect(() => {
        const root = document.documentElement;
        const isMobileLogin = () => window.innerWidth <= 1000;
        const viewportMeta = document.querySelector('meta[name="viewport"]');
        const previousViewport = viewportMeta?.getAttribute("content") ?? "";

        // Keyboard overlays content instead of resizing/pushing the page (Android Chrome+).
        if (viewportMeta && isMobileLogin()) {
            viewportMeta.setAttribute(
                "content",
                "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover, interactive-widget=overlays-content"
            );
        }

        const pinViewportHeight = () => {
            if (!isMobileLogin()) return;
            // Refresh height on resume so Safari toolbar show/hide does not leave a stale lock.
            root.style.setProperty("--login-screen-height", `${window.innerHeight}px`);
        };

        const getBodyLogin = () => document.querySelector(".bodylogin") as HTMLElement | null;

        const freezeScroll = () => {
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        };

        // Cancel iOS visual-viewport shift so the form never climbs with the keyboard.
        const pinAgainstKeyboard = () => {
            if (!root.classList.contains("login-keyboard-open")) return;
            freezeScroll();
            const body = getBodyLogin();
            const vv = window.visualViewport;
            if (!body || !vv) return;
            const y = vv.offsetTop || 0;
            body.style.transform = y ? `translate3d(0, ${y}px, 0)` : "";
        };

        const clearKeyboardPin = () => {
            const body = getBodyLogin();
            if (body) body.style.transform = "";
            freezeScroll();
        };

        const onWindowScroll = () => {
            if (!root.classList.contains("login-keyboard-open")) return;
            pinAgainstKeyboard();
        };

        const preventLoginScroll = (event: TouchEvent) => {
            const target = event.target;
            if (target instanceof HTMLElement && target.closest("input")) return;
            event.preventDefault();
        };

        const unlockLoginKeyboard = () => {
            window.setTimeout(() => {
                if (document.querySelector(".bodylogin input:focus")) return;
                root.classList.remove("login-keyboard-open");
                document.removeEventListener("touchmove", preventLoginScroll);
                clearKeyboardPin();
            }, 50);
        };

        const lockLoginKeyboard = () => {
            if (!isMobileLogin()) return;
            pinViewportHeight();
            root.classList.add("login-keyboard-open");
            pinAgainstKeyboard();
            document.removeEventListener("touchmove", preventLoginScroll);
            document.addEventListener("touchmove", preventLoginScroll, { passive: false });
        };

        const onFocusIn = (event: FocusEvent) => {
            if (!(event.target instanceof HTMLElement)) return;
            if (!event.target.closest(".bodylogin")) return;
            if (event.target.tagName !== "INPUT") return;
            lockLoginKeyboard();
            pinAgainstKeyboard();
            window.setTimeout(pinAgainstKeyboard, 0);
            window.setTimeout(pinAgainstKeyboard, 50);
            window.setTimeout(pinAgainstKeyboard, 120);
            window.setTimeout(pinAgainstKeyboard, 300);
        };

        const onFocusOut = () => unlockLoginKeyboard();

        const onVisualViewportChange = () => {
            if (!root.classList.contains("login-keyboard-open")) {
                // Toolbar show/hide after resume — refresh height without transforming the form.
                pinViewportHeight();
                return;
            }
            pinAgainstKeyboard();
        };

        const focusWithoutScroll = (event: TouchEvent | MouseEvent) => {
            if (!isMobileLogin()) return;
            const target = event.currentTarget;
            if (!(target instanceof HTMLInputElement)) return;
            event.preventDefault();
            target.focus({ preventScroll: true });
            lockLoginKeyboard();
            pinAgainstKeyboard();
        };

        pinViewportHeight();
        document.addEventListener("focusin", onFocusIn);
        document.addEventListener("focusout", onFocusOut);
        window.addEventListener("scroll", onWindowScroll, { passive: true });
        window.visualViewport?.addEventListener("resize", onVisualViewportChange);
        window.visualViewport?.addEventListener("scroll", onVisualViewportChange);

        const inputs = () => Array.from(document.querySelectorAll<HTMLInputElement>(".bodylogin input"));
        inputs().forEach((input) => {
            input.addEventListener("touchend", focusWithoutScroll, { passive: false });
        });

        return () => {
            document.removeEventListener("focusin", onFocusIn);
            document.removeEventListener("focusout", onFocusOut);
            document.removeEventListener("touchmove", preventLoginScroll);
            window.removeEventListener("scroll", onWindowScroll);
            window.visualViewport?.removeEventListener("resize", onVisualViewportChange);
            window.visualViewport?.removeEventListener("scroll", onVisualViewportChange);
            inputs().forEach((input) => {
                input.removeEventListener("touchend", focusWithoutScroll);
            });
            root.classList.remove("login-keyboard-open");
            root.style.removeProperty("--login-screen-height");
            clearKeyboardPin();
            if (viewportMeta && previousViewport) {
                viewportMeta.setAttribute("content", previousViewport);
            }
        };
    }, []);

    const handleAutofillAnimation = (e: React.AnimationEvent<HTMLInputElement>) => {
        if (e.animationName !== 'onAutoFillStart') return;
        dismissKeyboardAfterFaceId();
    };

    const getIP = async () => {
        try {
            const ipInfo = await clientIpInfo();
            const ipData: UserIPData | undefined = ipInfo.geo
                ? {
                    ip: ipInfo.ip,
                    country: ipInfo.geo.country,
                    regionName: ipInfo.geo.regionName,
                    city: ipInfo.geo.city,
                    zip: ipInfo.geo.zip,
                    lat: ipInfo.geo.lat,
                    lon: ipInfo.geo.lon,
                    query: ipInfo.geo.query,
                }
                : undefined;

            setUserIpData(ipData);

            if (ipData) {
                localStorage.setItem("userIpData", JSON.stringify(ipData));
            }
            await logUserActivity(userName, "successful login", ipData);

        } catch (err) {
            console.warn("IP info not available", err);
            try {
                await logUserActivity(userName, "successful login"
                );
            } catch (logErr) {
                console.error(logErr);
            }
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitting) return;
        dismissKeyboardAfterFaceId();
        const upperUserName = userName.toUpperCase();
        const upperPin = pin.toUpperCase();

        setIsSubmitting(true);
        try {
            const user = await login(upperUserName, upperPin);
            if (!user) return;

            dismissKeyboard();
            if (user.userType === "Player") {
                navigate("/home", { replace: true });
            } else if (
                user.userType === "masterAgent" ||
                user.userType === "subAgent"
            ) {
                navigate("/homeAdmin", { replace: true });
            } else {
                navigate("/", { replace: true });
            }
            getIP();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (

        <div className='background bodylogin'>
            <div
                className="login-mobile-fx"
                aria-hidden="true"
                style={{ ['--login-mobile-bg' as string]: `url(${loginMobileBg})` }}
            />
            <div className="logincontainer">

                <img className="logoLogin logoLogin--desktop" src={require("../images/logo.png")} alt="company logo" />
                <h2 className='title title--desktop teko'>Login</h2>

                <div className="login-mobile-header">
                    <div className="logoLogin--mobile-brand-slot" aria-hidden={!logoReady}>
                        {!logoReady && <span className="logoLogin--mobile-brand-skel" />}
                        <img
                            className={`logoLogin--mobile-brand${logoReady ? " is-ready" : ""}`}
                            src={loginBrandLogo}
                            alt="JockSports"
                            width={1054}
                            height={493}
                            decoding="async"
                            fetchpriority="high"
                            onLoad={() => setLogoReady(true)}
                        />
                    </div>
                    <span className="login-welcome-rule" aria-hidden="true" />
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                    <div className="input-container">
                        <FaUserAlt className="logouserNamen logouserNamen--user" />
                        <input
                            ref={userNameRef}
                            type="text"
                            id="userName"
                            name="username"
                            value={userName}
                            placeholder='Username'
                            required
                            readOnly={isSubmitting}
                            onChange={(e) => setUserName(e.target.value)}
                            autoComplete="username"
                        />
                    </div>
                    <div className="input-container mt-3">
                        <RiLockPasswordFill className="logouserNamen logouserNamen--lock" />
                        <input
                            ref={pinRef}
                            type="password"
                            id="password"
                            name="password"
                            value={pin}
                            placeholder='Password'
                            required
                            readOnly={isSubmitting}
                            onChange={(e) => setPin(e.target.value)}
                            onAnimationStart={handleAutofillAnimation}
                            autoComplete="current-password"
                        />
                    </div>
                    {error && <p className="login-error">{error.message}</p>}
                    <div className='p-login'>
                        <span>
                            <Link to="/pass" className='a-pass'>Forgot Password?</Link>
                        </span>
                    </div>

                    <button type="submit" className='buttonlogin login-submit' disabled={isSubmitting}>
                        <span className="login-submit__desktop">Login</span>
                        <span className="login-submit__mobile">SIGN IN</span>
                    </button>
                </form>
            </div>
            {isSubmitting && (
                <div className="loader-overlay-proyect">
                    <FaSyncAlt className="loader-icon-proyect" />
                </div>
            )}
        </div>
    );
};


export default Login;
